/**
 * Central curriculum definition for IB Computer Science Theme B.
 *
 * isAvailable: true  → unit is clickable in at least one language track.
 * isAvailable: false → coming soon, shown locked in all tracks.
 *
 * languages: string[]
 *   Lists which language tracks have backend content for this topic.
 *   Use topicAvailableForLang(topic, lang) to query at runtime.
 *   The backend resolves the concrete unit id (e.g. 'b3_1' + 'java' →
 *   'b3_1_java') so unitId always stores the base / language-neutral key.
 */

export const THEME_B = [
  {
    id:    'B1',
    title: 'B1 Computational thinking',
    topics: [
      {
        id:              'B1.1',
        title:           'B1.1 Approaches to computational thinking',
        unitId:          'b1_1',
        isAvailable:     true,
        languages: ['python', 'java'],
      },
    ],
  },
  {
    id:    'B2',
    title: 'B2 Programming',
    topics: [
      {
        id:              'B2.1',
        title:           'B2.1 Programming fundamentals',
        unitId:          'b2_1',
        isAvailable:     true,
        languages: ['python', 'java'],
      },
      {
        id:              'B2.2',
        title:           'B2.2 Data structures',
        unitId:          'b2_2',
        isAvailable:     true,
        languages: ['python', 'java'],
      },
      {
        id:              'B2.3',
        title:           'B2.3 Programming constructs',
        unitId:          'b2_3',
        isAvailable:     true,
        languages: ['python', 'java'],
      },
      {
        id:              'B2.4',
        title:           'B2.4 Programming algorithms',
        unitId:          'b2_4',
        isAvailable:     true,
        languages: ['python', 'java'],
      },
      {
        id:              'B2.5',
        title:           'B2.5 File processing',
        unitId:          'b2_5',
        isAvailable:     true,
        languages: ['python', 'java'],
      },
    ],
  },
  {
    id:    'B3',
    title: 'B3 Object-oriented programming',
    topics: [
      {
        id:              'B3.1',
        title:           'B3.1 Fundamentals of OOP for a single class',
        unitId:          'b3_1',
        isAvailable:     true,
        // Available in both tracks: b3_1 (Python) and b3_1_java (Java).
        languages: ['python', 'java'],
      },
      {
        id:              'B3.2',
        title:           'B3.2 Fundamentals of OOP for multiple classes (HL only)',
        unitId:          'b3_2',
        isAvailable:     true,
        languages: ['python', 'java'],
        hlOnly:          true,
      },
    ],
  },
  {
    id:    'B4',
    title: 'B4 Abstract data types — HL only',
    hlOnly: true,
    topics: [
      {
        id:              'B4.1',
        title:           'B4.1 Fundamentals of ADTs (HL only)',
        unitId:          'b4_1',
        isAvailable:     true,
        languages: ['python', 'java'],
        hlOnly:          true,
      },
    ],
  },
  {
    id:    'P2',
    title: 'Paper 2 exam practice',
    topics: [
      // Coding scenarios — each P2 scenario is its own question (backend units
      // p2_1…p2_5, registered in curriculum/__init__.py). `p2group` drives the
      // grouping shown in the dashboard; MCQ and combined groups slot in later.
      { id: 'P2.1', title: 'Hospital appointments', unitId: 'p2_1', isAvailable: true, languages: ['python', 'java'], p2group: 'coding' },
      { id: 'P2.2', title: 'Library loans',          unitId: 'p2_2', isAvailable: true, languages: ['python', 'java'], p2group: 'coding' },
      { id: 'P2.3', title: 'Game leaderboard',       unitId: 'p2_3', isAvailable: true, languages: ['python', 'java'], p2group: 'coding', hlOnly: true },
      { id: 'P2.4', title: 'Airline bookings',       unitId: 'p2_4', isAvailable: true, languages: ['python', 'java'], p2group: 'coding', hlOnly: true },
      { id: 'P2.5', title: 'Support desk',           unitId: 'p2_5', isAvailable: true, languages: ['python', 'java'], p2group: 'coding' },

      // Multiple-choice questions — the non-coding parts of a Paper 2 (state the
      // output, Big O, trace, explain). No backend unit: these open a client-
      // marked quiz (Paper2Quiz) via `mcqSet`, routed in CompetencyDashboard.
      { id: 'P2.M1', title: 'Core skills', mcqSet: 'p2_core_skills', isAvailable: true, languages: ['python', 'java'], p2group: 'mcq' },
    ],
  },
];

/** Flat list of all topics for quick lookup by id. */
export const ALL_TOPICS = THEME_B.flatMap(section => section.topics);

/** Find a topic by its syllabus id (e.g. 'B2.1'). */
export function findTopic(id) {
  return ALL_TOPICS.find(t => t.id === id) || null;
}

/**
 * Returns true when a topic has backend content for the given language track.
 * Falls back to isAvailable when languages is absent (legacy entries).
 *
 * @param {object} topic  - A topic entry from THEME_B
 * @param {string} lang   - Active language track, e.g. 'python' | 'java'
 */
export function topicAvailableForLang(topic, lang) {
  if (!topic) return false;
  if (!topic.languages) return topic.isAvailable ?? false;
  return topic.languages.includes(lang);
}

/**
 * True for the Paper 2 exam-practice topics. These are gated behind completion
 * of the core Theme B units in the active language track (see isPaper2Unlocked).
 */
export function isPaper2Topic(topic) {
  return !!topic && typeof topic.id === 'string' && topic.id.startsWith('P2');
}

/**
 * Resolve a topic's language-neutral unitId to the concrete backend variant for
 * a track (e.g. 'b3_1' + 'java' → 'b3_1_java'). Mirrors the backend resolver.
 */
export function resolveUnitId(topic, lang) {
  if (!topic?.unitId) return null;
  return lang === 'java' && !topic.unitId.endsWith('_java')
    ? `${topic.unitId}_java`
    : topic.unitId;
}

/**
 * Paper 2 practice unlocks for a language track only once every core (non
 * Paper-2) Theme B unit available in that track is fully complete. HL-only units
 * are excluded for SL learners, who are not expected to complete them.
 *
 * @param {object} units  playerData.units, keyed by resolved unit id
 * @param {string} lang   'python' | 'java'
 * @param {string} level  'sl' | 'hl'
 * @returns {boolean}
 */
export function isPaper2Unlocked(units, lang, level = 'hl') {
  if (!units) return false;
  const core = ALL_TOPICS.filter(t =>
    t.unitId &&
    !isPaper2Topic(t) &&
    topicAvailableForLang(t, lang) &&
    !(level === 'sl' && t.hlOnly)
  );
  if (core.length === 0) return false;
  return core.every(t => {
    const u = units[resolveUnitId(t, lang)];
    const total = u?.totalExercises ?? u?.total_exercises ?? 0;
    const done  = u?.completedExercises ?? u?.completed_exercises ?? 0;
    return total > 0 && done >= total;
  });
}

/**
 * Whether Paper 2 practice is open for this player.
 *
 * Paper 2 is now available from the start — it is a Premium feature, gated by
 * entitlement like the rest of the paid content (see isTopicAccessible, which
 * Premium-locks it for free accounts and the server enforces with a 402). It is
 * no longer gated behind 100% Theme B completion, so this always returns true;
 * the completion helper isPaper2Unlocked is retained for reference/possible reuse.
 *
 * @param {object} playerData  the mastery/player payload
 * @param {string} lang        'python' | 'java'
 * @param {string} level       'sl' | 'hl'
 */
export function isPaper2Accessible(playerData, lang, level) { // eslint-disable-line no-unused-vars
  return true;
}

// ── Freemium entitlement (mirrors backend app/entitlement.py) ──────────────────
// `playerData.entitled` (full Premium access) and the free_* allowlists come from
// the dashboard payload, so the UI reflects the server's truth and never hard-codes
// the tier boundary. The fallbacks match the backend defaults (B1 + A1, Python) for
// the brief window before the first dashboard load.

/** Top-level coding prefix of a unitId, e.g. 'b2_3_java' -> 'b2'. */
export function codingPrefix(unitId) {
  return (unitId || '').split('_', 1)[0].toLowerCase();
}

/**
 * True when a Theme B topic is reachable on the free tier for a language track:
 * a free-list coding prefix AND a free language (Python by default).
 */
export function isFreeTopic(topic, lang, playerData) {
  if (!topic) return false;
  const codingFree = playerData?.free_coding_prefixes ?? ['b1'];
  const langFree   = playerData?.free_languages ?? ['python'];
  return codingFree.includes(codingPrefix(topic.unitId)) &&
         langFree.includes((lang || '').toLowerCase());
}

/**
 * Whether a Theme B topic should be open for this player. Entitled (Premium) players
 * get everything; everyone else only the free tier. Paper 2's separate completion
 * gate (isPaper2Accessible) still applies on top for entitled players.
 */
export function isTopicAccessible(playerData, topic, lang) {
  if (playerData?.entitled) return true;
  return isFreeTopic(topic, lang, playerData);
}

/** Free-tier check for a Theme A theory scope id, e.g. 'A1' / 'A1.2' -> top-level 'A1'. */
export function isFreeTheoryScope(scope, playerData) {
  const theoryFree = playerData?.free_theory_prefixes ?? ['A1'];
  const top = (scope || '').split('.', 1)[0].toUpperCase();
  return theoryFree.includes(top);
}

/** Whether a Theme A scope should be open: entitled players get all; else free only. */
export function isTheoryAccessible(playerData, scope) {
  if (playerData?.entitled) return true;
  return isFreeTheoryScope(scope, playerData);
}

/**
 * The account plan pill from the entitlement flags on playerData — the ladder is
 * Free · Premium · Teacher · School (+ Staff for admins). Only Free/Premium/Teacher
 * are determinable today; 'school' is reserved for a future schools-table flag.
 * @returns {{key: string, label: string}}
 */
export function accountPlan(playerData) {
  if (!playerData?.entitled) return { key: 'free',    label: 'Free' };
  if (playerData.is_admin)   return { key: 'staff',   label: 'Staff' };
  if (playerData.is_teacher) return { key: 'teacher', label: 'Teacher' };
  return { key: 'premium', label: 'Premium' };
}
